import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useSite } from '../useSite';
import { SERVICE_KEYS } from '../routes';
import { Link } from 'react-router-dom';
import { fieldClass, labelClass, validity } from './TestimonialForm';

const API = process.env.REACT_APP_BACKEND_URL;

export const IntakeForm = () => {
  const { t, lang, p } = useSite();
  const b = t.book;
  const f = b.form;
  const empty = { name: '', email: '', phone: '', client_category: '', country: '', service: '', preferred_language: lang, message: '', website: '', consent: false };
  const [data, setData] = useState(empty);
  const [state, setState] = useState({ busy: false, msg: '', ok: false });
  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });
  const { search } = useLocation();
  useEffect(() => {
    const plan = new URLSearchParams(search).get('plan');
    const name = { basic: 'Basic', 'basic-plus': 'Basic Plus', quarterly: 'Quarterly', monthly: 'Monthly' }[plan];
    if (name) setData((d) => ({ ...d, service: 'accounting', message: d.message || `${f.planPrefix} ${name}.` }));
  }, [search, f.planPrefix]);
  const v = validity(f.required, f.invalidEmail);
  const services = [...SERVICE_KEYS.map((k) => ({ value: k, label: t.services[k].name })), b.serviceOther];

  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, msg: '', ok: false });
    try {
      await axios.post(`${API}/api/contact`, { ...data, phone: data.phone || null }, { timeout: 30000 });
      setData(empty);
      setState({ busy: false, msg: f.success, ok: true });
    } catch (err) {
      setState({ busy: false, msg: err.response?.status === 429 ? f.rateLimited : f.error, ok: false });
    }
  };

  const select = (k, label, options, testId) => (
    <label className="block"><span className={labelClass}>{label}</span>
      <select required {...v} data-testid={testId} value={data[k]} onChange={set(k)} className={fieldClass}>
        {k !== 'preferred_language' && <option value="">{f.select}</option>}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="intake-form">
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hp-field" value={data.website} onChange={set('website')} />
      <div className="grid gap-5 sm:grid-cols-2">
        <label className="block"><span className={labelClass}>{f.name}</span>
          <input data-testid="intake-name-input" required {...v} maxLength={100} value={data.name} onChange={set('name')} className={fieldClass} />
        </label>
        <label className="block"><span className={labelClass}>{f.email}</span>
          <input data-testid="intake-email-input" type="email" required {...v} value={data.email} onChange={set('email')} className={fieldClass} />
        </label>
        <label className="block"><span className={labelClass}>{f.phone}</span>
          <input data-testid="intake-phone-input" type="tel" maxLength={20} value={data.phone} onChange={set('phone')} className={fieldClass} />
        </label>
        <label className="block"><span className={labelClass}>{f.country}</span>
          <input data-testid="intake-country-input" required {...v} maxLength={80} value={data.country} onChange={set('country')} className={fieldClass} />
        </label>
        {select('client_category', f.category, b.categories, 'intake-category-select')}
        {select('service', f.service, services, 'intake-service-select')}
        {select('preferred_language', f.language, b.languages, 'intake-language-select')}
      </div>
      <label className="block"><span className={labelClass}>{f.message}</span>
        <textarea data-testid="intake-message-input" required {...v} maxLength={2000} rows={5} value={data.message} onChange={set('message')} placeholder={f.messagePlaceholder} className={fieldClass} />
      </label>
      <p className="border-l-2 border-moss-green bg-bone-light px-4 py-3 text-xs leading-relaxed text-cafe-noir/80" data-testid="intake-sensitive-notice">{b.notice}</p>
      <label className="flex items-start gap-3 text-sm leading-relaxed text-cafe-noir/85" data-testid="intake-consent-label">
        <input
          type="checkbox" required data-testid="intake-consent-checkbox" checked={data.consent}
          onChange={(e) => { e.target.setCustomValidity(''); setData({ ...data, consent: e.target.checked }); }}
          onInvalid={(e) => e.target.setCustomValidity(f.consentRequired)}
          className="mt-1 h-4 w-4 flex-shrink-0 accent-kombu-green"
        />
        <span>{f.consent} <Link to={p('privacy')} target="_blank" rel="noopener noreferrer" className="text-kombu-green underline underline-offset-2" data-testid="intake-privacy-link">{f.consentLink}</Link>.</span>
      </label>
      <p className="text-xs text-cafe-noir/70" data-testid="intake-not-confirmed">{b.notConfirmed}</p>
      {state.msg && <p role="status" data-testid="intake-form-status" className={`text-sm ${state.ok ? 'text-kombu-green' : 'text-red-700'}`}>{state.msg}</p>}
      <button type="submit" disabled={state.busy} data-testid="intake-submit-btn"
        className="w-full rounded-[2px] bg-kombu-green px-6 py-4 text-sm font-semibold text-bone-light transition-colors hover:bg-kombu-green-dark disabled:opacity-60 sm:w-auto">
        {state.busy ? f.sending : f.submit}
      </button>
    </form>
  );
};
